/**
 * Baselines — helpers for computing the comparison targets that the
 * visual-indicator components (`DeltaChip`, `RankBadge`) compare
 * entities against.
 *
 * Three baselines are supported:
 *
 *   - countyAvg : the tenant-wide average (from `/voters/stats`). Cached
 *     for the session in a module-level promise so multiple views don't
 *     re-fetch. Use via the `useCountyBaselines` hook.
 *   - parentAvg : the aggregated average of a precinct's subregion,
 *     or a subregion's LD, or an LD's county. Computed locally by the
 *     caller from data it already has in scope and passed into the
 *     indicator components directly.
 *   - peerMedian : the median across a list of peer entities (e.g.
 *     all precincts inside the same LD). Computed locally via
 *     `computePeerBaselines()`.
 *
 * Design note: we deliberately keep baselines passive — they're pure
 * number → number shapes, not React context — so that any view can mix
 * and match whichever baselines it has available without requiring a
 * provider further up the tree. Only the county-wide fetch is cached
 * globally, and only because it's the one expensive network call.
 */
import { useEffect, useState } from 'react';
import client from '@/api/client';

// -------------------------------------------------------------------------
// Shared shapes
// -------------------------------------------------------------------------

export type CountyBaselines = {
  /** Tenant-wide average voter score (0 – 100). */
  avgScore: number;
  /** Tenant-wide active-voter percentage. */
  activePct: number;
  /** Share of voters in the Anchor tier (Super Voter %). */
  superVoterPct: number;
  /** Share of voters with any primary-election history. */
  primaryVoterPct: number;
  /** Raw total voter count — not a baseline per se, but useful for captions. */
  totalVoters: number;
};

export type PeerBaselines = {
  avgScore: number;
  activePct: number;
  superVoterPct: number;
  primaryVoterPct: number;
  /** Number of entities in the peer group (for "rank of N" labels). */
  n: number;
};

// -------------------------------------------------------------------------
// County-wide fetch (cached for the session)
// -------------------------------------------------------------------------

type RawVoterStats = {
  total_voters: number;
  active_voters: number;
  average_score: number;
  by_tier: Record<string, number>;
  primary_voters: number;
  high_scorers: number;
};

let cachedPromise: Promise<CountyBaselines> | null = null;

/**
 * Fetch + cache the tenant-wide baseline stats for the session. Subsequent
 * calls return the same in-flight promise, so a ten-view session only hits
 * `/voters/stats` once.
 */
export function fetchCountyBaselines(): Promise<CountyBaselines> {
  if (cachedPromise) return cachedPromise;
  cachedPromise = client
    .get<RawVoterStats>('/voters/stats')
    .then((res) => {
      const s = res.data;
      const anchor = s.by_tier?.SUPER_VOTER ?? 0;
      return {
        avgScore: s.average_score ?? 0,
        activePct: s.total_voters ? (s.active_voters * 100) / s.total_voters : 0,
        superVoterPct: s.total_voters ? (anchor * 100) / s.total_voters : 0,
        primaryVoterPct: s.total_voters
          ? (s.primary_voters * 100) / s.total_voters
          : 0,
        totalVoters: s.total_voters,
      };
    })
    .catch((err) => {
      // Don't poison the cache — let the next caller retry.
      cachedPromise = null;
      throw err;
    });
  return cachedPromise;
}

/**
 * Hook variant. Returns { baselines, loading, error }. Any view can call
 * this without worrying about redundant fetches — the module-level cache
 * makes it free after the first call.
 */
export function useCountyBaselines(): {
  baselines: CountyBaselines | null;
  loading: boolean;
  error: string | null;
} {
  const [baselines, setBaselines] = useState<CountyBaselines | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCountyBaselines()
      .then((b) => {
        if (!cancelled) {
          setBaselines(b);
          setLoading(false);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to load county baselines');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { baselines, loading, error };
}

// -------------------------------------------------------------------------
// Peer-group helpers (no network — pure client-side computation)
// -------------------------------------------------------------------------

type PeerRow = {
  avg_score?: number | null;
  active_pct?: number | null;
  super_voter_pct?: number | null;
  primary_voter_pct?: number | null;
};

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Compute peer-median baselines across a list of precinct rollups.
 * Null/undefined values are filtered out per metric, so a row that's
 * missing one stat doesn't drag everything toward zero.
 */
export function computePeerBaselines(rows: PeerRow[]): PeerBaselines {
  const pick = (getter: (r: PeerRow) => number | null | undefined) =>
    rows
      .map(getter)
      .filter((v): v is number => v != null && !Number.isNaN(v));
  return {
    avgScore: median(pick((r) => r.avg_score)),
    activePct: median(pick((r) => r.active_pct)),
    superVoterPct: median(pick((r) => r.super_voter_pct)),
    primaryVoterPct: median(pick((r) => r.primary_voter_pct)),
    n: rows.length,
  };
}

/**
 * Rank an entity within a list of peer rows by a given metric. Returns
 * 1-indexed rank + the total count. Higher values = lower rank number
 * (rank 1 is the best) when `direction === 'higher-better'`. Ties share
 * the lower rank number (standard competition ranking).
 */
export function rankWithin(
  rows: PeerRow[],
  getValue: (r: PeerRow) => number | null | undefined,
  target: number | null | undefined,
  direction: 'higher-better' | 'lower-better' = 'higher-better',
): { rank: number; total: number } {
  const total = rows.length;
  if (target == null || Number.isNaN(target)) return { rank: total, total };
  let better = 0;
  for (const row of rows) {
    const v = getValue(row);
    if (v == null || Number.isNaN(v)) continue;
    if (direction === 'higher-better' ? v > target : v < target) better += 1;
  }
  return { rank: better + 1, total };
}
